import type { NavMenuLink } from '@/data/shared/navMenu';

/**
 * ### NavLinkAttributes
 *
 * Anchor attributes derived from a navigation link, modelled so that
 * `rel` can only be present together with the new-window `target`.
 * `isExternal` doubles as the switch for the visible external-link mark.
 */
export type NavLinkAttributes =
  | { isExternal: true; target: '_blank'; rel: 'noopener noreferrer' }
  | { isExternal: false; target: undefined; rel: undefined };

/**
 * ### resolveNavLinkAttributes
 *
 * Turns the declared `target` of a navigation link into the anchor attributes
 * every header and footer link renders with, so external links always open in
 * a new window with the opener severed.
 * Externality is decided by the declaration alone; the URL is never inspected.
 *
 * @param link Navigation link whose target declaration to read
 * @returns Attributes to spread onto the anchor, plus whether to show the external mark
 */
export function resolveNavLinkAttributes(link: NavMenuLink): NavLinkAttributes {
  if (link.target === '_blank') {
    return { isExternal: true, target: '_blank', rel: 'noopener noreferrer' };
  }

  return { isExternal: false, target: undefined, rel: undefined };
}
